import { FC, useState, useEffect, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import GetUserProfileUseCase, { GetUserProfileUseCaseName } from "../../../../../domain/use_cases/user/GetUserProfileUseCase";
import UpdateUserUseCase, { UpdateUserUseCaseName } from "../../../../../domain/use_cases/user/UpdateUserUseCase";
import SearchUserBUnitsUseCase, { SearchUserBUnitsUseCaseName } from "../../../../../domain/use_cases/user/SearchUserBUnitsUseCase";
import SearchUserPositionsUseCase, { SearchUserPositionsUseCaseName } from "../../../../../domain/use_cases/user/SearchUserPositionsUseCase";
import UserProfileEntity from "../../../../../domain/entities/UserProfileEntity";
import di from "../../../../../di/DependencyInjection";
import LoadingComponent from "../../../../components/LoadingComponent/LoadingComponent";
import NotFoundComponent from "../../../../components/notFound/NotFoundComponent";
import { isRight } from "fp-ts/lib/Either";
import { Alert, Button, SpaceBetween } from "@cloudscape-design/components";
import { SelectProps } from "@cloudscape-design/components/select";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import ModalsContext from "../../../../../domain/providers/modal/ModalsContext";
import ModalsContextType from "../../../../../domain/providers/modal/ModalsContextType";
import ProfileHeader from "./components/ProfileHeader";
import InlineEditField from "./components/InlineEditField";
import InlineSelectField from "./components/InlineSelectField";
import BucketBreadcrumb from "./components/BucketBreadcrumb";
import GroupsDisplay from "./components/GroupsDisplay";
import PermissionMatrix from "./components/PermissionMatrix";
import MissingDataBanner from "./components/MissingDataBanner";

/**
 * Derive location, client, supervisor from bucket_path.
 * Bucket path segments follow: itel / <location> / <client> / <supervisor>
 */
function deriveBucketFields(profile: UserProfileEntity): { location: string; client: string; supervisor: string } {
    const path = profile.bucket_path;
    if (!path || path.length === 0) {
        return { location: "", client: "", supervisor: "" };
    }
    // bucket_path array: [itel, location, client, supervisor] (first is always root "itel")
    const names = path.map(s => s.name);
    return {
        location: names[1] || "",
        client: names[2] || "",
        supervisor: names[3] || "",
    };
}

const UserProfilePage: FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const [profile, setProfile] = useState<UserProfileEntity | undefined | null>(undefined);
    const [error, setError] = useState<string | null>(null);
    const [businessUnitOptions, setBusinessUnitOptions] = useState<SelectProps.Option[]>([]);
    const [positionOptions, setPositionOptions] = useState<SelectProps.Option[]>([]);
    const [optionsLoading, setOptionsLoading] = useState(true);
    const { addToast } = useContext(ModalsContext) as ModalsContextType;

    // Fetch dropdown options on mount
    useEffect(() => {
        const loadOptions = async () => {
            setOptionsLoading(true);
            try {
                const [buResult, posResult] = await Promise.all([
                    di.get<SearchUserBUnitsUseCase>(SearchUserBUnitsUseCaseName).call(),
                    di.get<SearchUserPositionsUseCase>(SearchUserPositionsUseCaseName).call(),
                ]);

                if (isRight(buResult)) {
                    const buOptions: SelectProps.Option[] = (buResult.right as any[]).map((bu: any) => ({
                        label: bu.name,
                        value: bu.name,
                    }));
                    setBusinessUnitOptions(buOptions);
                }

                if (isRight(posResult)) {
                    const posOptions: SelectProps.Option[] = (posResult.right as any[]).map((pos: any) => ({
                        label: pos.name,
                        value: pos.name,
                    }));
                    setPositionOptions(posOptions);
                }
            } catch {
                // Options will remain empty; fields fall back to text display
            } finally {
                setOptionsLoading(false);
            }
        };

        loadOptions();
    }, []);

    const handleFieldUpdate = useCallback(async (fieldName: string, newValue: string): Promise<void> => {
        if (!profile) return;

        const { location, client, supervisor } = deriveBucketFields(profile);

        // Map frontend field names to backend expected keys
        const fieldNameMap: Record<string, string> = {
            firstname: "first name",
            lastname: "last name",
        };
        const backendFieldName = fieldNameMap[fieldName] || fieldName;

        // PUT /usersetup requires the full payload with all required fields
        // Explicitly compute values: use newValue for the field being edited, profile value for others
        const firstNameValue = backendFieldName === "first name" ? newValue : (profile.firstname || "");
        const lastNameValue = backendFieldName === "last name" ? newValue : (profile.lastname || "");

        const fullPayload: Record<string, any> = {
            email: profile.email,
            role: profile.role_name,
            position: backendFieldName === "position" ? newValue : (profile.position || ""),
            business_unit: backendFieldName === "business_unit" ? newValue : (profile.business_unit || ""),
            timezone: profile.timezone || "",
            location,
            client,
            supervisor,
            hr_id: profile.employee_no || "",
            "first name": firstNameValue,
            "last name": lastNameValue,
            firstname: firstNameValue,
            lastname: lastNameValue,
            telephone_number: backendFieldName === "telephone_number" ? newValue : (profile.telephone_number || ""),
            itel_email: backendFieldName === "itel_email" ? newValue : (profile.itel_email || ""),
        };

        const payload = [fullPayload];
        const result = await di.get<UpdateUserUseCase>(UpdateUserUseCaseName).call(payload);

        if (isRight(result)) {
            setProfile(prev => prev ? { ...prev, [fieldName]: newValue } : prev);
        } else {
            addToast(result.left.message ?? "Update failed", "error", undefined);
            throw new Error(result.left.message ?? "Update failed");
        }
    }, [profile, addToast]);

    const fetchProfile = useCallback(async () => {
        if (!userId) {
            setProfile(null);
            return;
        }

        setProfile(undefined);
        setError(null);

        const useCase = di.get<GetUserProfileUseCase>(GetUserProfileUseCaseName);
        const result = await useCase.call(userId as string);

        if (isRight(result)) {
            setProfile(result.right);
        } else {
            setError(result.left.message ?? "An unexpected error occurred");
            setProfile(null);
        }
    }, [userId]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    if (profile === undefined) return <LoadingComponent />;

    if (error && profile === null) {
        return (
            <SpaceBetween size="m" direction="vertical">
                <Alert type="error">{error}</Alert>
                <Button onClick={fetchProfile}>Retry</Button>
            </SpaceBetween>
        );
    }

    if (profile === null) return <NotFoundComponent />;

    return (
        <SpaceBetween size="l" direction="vertical">
            <MissingDataBanner profile={profile} />

            <Container header={<Header variant="h2">Profile</Header>}>
                <SpaceBetween size="l" direction="vertical">
                    <ProfileHeader
                        firstName={profile.firstname}
                        lastName={profile.lastname}
                        roleName={profile.role_name}
                        isActive={profile.is_active}
                        profilePictureUrl={profile.profile_picture}
                    />

                    <ColumnLayout columns={2}>
                        <SpaceBetween size="s" direction="vertical">
                            <InlineEditField label="First Name" value={profile.firstname} onSave={(v) => handleFieldUpdate("firstname", v)} />
                            <InlineEditField label="Last Name" value={profile.lastname} onSave={(v) => handleFieldUpdate("lastname", v)} />
                            <InlineSelectField
                                label="Business Unit"
                                value={profile.business_unit || ""}
                                options={businessUnitOptions}
                                loading={optionsLoading}
                                placeholder="Select business unit"
                                onSave={(v) => handleFieldUpdate("business_unit", v)}
                            />
                            <InlineSelectField
                                label="Position"
                                value={profile.position || ""}
                                options={positionOptions}
                                loading={optionsLoading}
                                placeholder="Select position"
                                onSave={(v) => handleFieldUpdate("position", v)}
                            />
                        </SpaceBetween>
                        <SpaceBetween size="s" direction="vertical">
                            <InlineEditField label="Timezone" value={profile.timezone || ""} onSave={(v) => handleFieldUpdate("timezone", v)} />
                            <InlineEditField label="Phone" value={profile.telephone_number || ""} onSave={(v) => handleFieldUpdate("telephone_number", v)} />
                            <InlineEditField label="itel Email" value={profile.itel_email || ""} onSave={(v) => handleFieldUpdate("itel_email", v)} />
                            <InlineEditField label="Email" value={profile.email} disabled onSave={async () => {}} />
                            <InlineEditField label="HR ID" value={profile.employee_no} disabled onSave={async () => {}} />
                        </SpaceBetween>
                    </ColumnLayout>
                </SpaceBetween>
            </Container>

            <Container header={<Header variant="h2">Organization</Header>}>
                <SpaceBetween size="m" direction="vertical">
                    <div>
                        <h4 style={{ margin: "0 0 8px 0" }}>Bucket Path</h4>
                        <BucketBreadcrumb bucketPath={profile.bucket_path} />
                    </div>
                    <div>
                        <h4 style={{ margin: "0 0 8px 0" }}>Groups</h4>
                        <GroupsDisplay groups={profile.groups} />
                    </div>
                </SpaceBetween>
            </Container>

            <Container header={<Header variant="h2">Permissions</Header>}>
                <PermissionMatrix permissions={profile.permissions} />
            </Container>
        </SpaceBetween>
    );
};

export default UserProfilePage;
